/**
 * Derive course-path pills and program filters from the programs API list.
 */
import type { Program } from '@/types/program.types';

/** Stored program path (`null` is treated as standard). */
export type ProgramCoursePath = 'standard' | 'sport';

/** Pill value: a real path, or `all` when several paths exist. */
export type ProgramCascadePath = ProgramCoursePath | 'all';

/** Resolution used by {@link ProgramCascadeFields}. */
export interface ProgramCascadeResolution {
  uniquePaths: ProgramCoursePath[];
  showCoursePath: boolean;
  pills: ProgramCascadePath[];
  defaultPath: ProgramCascadePath | undefined;
}

/** Display order for course-path pills (classic before sport). */
const PATH_ORDER: readonly ProgramCoursePath[] = ['standard', 'sport'];

/** Sort rank used when listing programs under the `all` pill. */
const PATH_SORT: Record<ProgramCoursePath, number> = { standard: 0, sport: 1 };

/** Treat a missing `coursePathType` as classic. */
export function normalizeProgramCoursePath(
  program: Pick<Program, 'coursePathType'>,
): ProgramCoursePath {
  return program.coursePathType === 'sport' ? 'sport' : 'standard';
}

/** Distinct paths in the list, classic before sport. */
export function getUniqueCoursePaths(
  programs: ReadonlyArray<Pick<Program, 'coursePathType'>>,
): ProgramCoursePath[] {
  const seen = new Set<ProgramCoursePath>();
  for (const program of programs) {
    seen.add(normalizeProgramCoursePath(program));
  }
  return PATH_ORDER.filter((path) => seen.has(path));
}

/**
 * One path → hide pills and force that path.
 * Two or more → show those paths plus `all`; default classic when present.
 */
export function resolveProgramCascade(
  programs: ReadonlyArray<Pick<Program, 'coursePathType'>>,
): ProgramCascadeResolution {
  const uniquePaths = getUniqueCoursePaths(programs);
  if (uniquePaths.length === 0) {
    return { uniquePaths, showCoursePath: false, pills: [], defaultPath: undefined };
  }
  if (uniquePaths.length === 1) {
    return {
      uniquePaths,
      showCoursePath: false,
      pills: uniquePaths,
      defaultPath: uniquePaths[0],
    };
  }
  return {
    uniquePaths,
    showCoursePath: true,
    pills: [...uniquePaths, 'all'],
    defaultPath: uniquePaths.includes('standard') ? 'standard' : uniquePaths[0],
  };
}

/** Programs for the selected pill. `all` keeps classic before sport. */
export function filterProgramsByCoursePath(
  programs: readonly Program[],
  path: string | undefined,
): Program[] {
  if (!path) return [];
  if (path === 'all') {
    return [...programs].sort(
      (left, right) =>
        PATH_SORT[normalizeProgramCoursePath(left)] - PATH_SORT[normalizeProgramCoursePath(right)],
    );
  }
  if (path === 'standard') {
    return programs.filter((program) => normalizeProgramCoursePath(program) === 'standard');
  }
  if (path === 'sport') {
    return programs.filter((program) => normalizeProgramCoursePath(program) === 'sport');
  }
  return [];
}

/** Whether the stored path is still one of the visible pills. */
export function isProgramCascadePathValid(
  path: string | undefined,
  pills: readonly ProgramCascadePath[],
): path is ProgramCascadePath {
  return !!path && pills.includes(path as ProgramCascadePath);
}

/** String ids from a form `programs` field (strings or `{ programId | id }`). */
export function selectedProgramIds(selected: unknown): string[] {
  if (!Array.isArray(selected)) return [];
  return selected.flatMap((item) => {
    if (typeof item === 'string' && item) return [item];
    if (!item || typeof item !== 'object') return [];
    const row = item as { programId?: unknown; id?: unknown };
    const id = row.programId ?? row.id;
    return typeof id === 'string' && id ? [id] : [];
  });
}

/**
 * Course-path pill implied by selected program ids against the programs catalog.
 * Stale onboarding `coursePath` is ignored — ZupDeFoot (`sport`) wins over a stored `standard`.
 */
export function resolveCoursePathFromSelectedPrograms(
  programs: ReadonlyArray<Pick<Program, 'id' | 'coursePathType'>>,
  selectedIds: readonly string[] | undefined,
): ProgramCascadePath | undefined {
  if (!selectedIds?.length) return undefined;
  const idSet = new Set(selectedIds.filter(Boolean));
  if (idSet.size === 0) return undefined;
  const selected = programs.filter((row) => idSet.has(row.id));
  if (selected.length === 0) return undefined;
  const uniquePaths = getUniqueCoursePaths(selected);
  if (uniquePaths.length === 1) return uniquePaths[0];
  if (uniquePaths.length > 1) return 'all';
  return undefined;
}

/** Overwrite form `coursePath` from the programs catalog when a selection matches. */
export function applyCatalogCoursePath<T extends { coursePath?: unknown; programs?: unknown }>(
  form: T,
  programs: ReadonlyArray<Pick<Program, 'id' | 'coursePathType'>> | undefined,
): T {
  if (!programs?.length) return form;
  const derived = resolveCoursePathFromSelectedPrograms(
    programs,
    selectedProgramIds(form.programs),
  );
  if (derived) form.coursePath = derived;
  return form;
}
